<script setup>
import { ref, computed, watch } from 'vue';
import { usePipelinePermissions } from 'dashboard/composables/pipelines/usePipelinePermissions';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: v => ['create', 'edit'].includes(v),
  },
  role: { type: Object, default: null },
});

const emit = defineEmits(['save', 'close']);
const { editablePermissionKeys, isPro } = usePipelinePermissions();

const name = ref('');
const description = ref('');
const selectedKeys = ref([]);

const isEditingDefaultAgentRole = computed(
  () => props.role?.default_agent_role && props.role?.system_role
);

const isReadOnly = computed(
  () => props.role?.system_role && !props.role?.default_agent_role
);

const availableKeys = computed(() => editablePermissionKeys.value);

const KEY_GROUPS = [
  {
    label: 'Conversas',
    keys: [
      {
        key: 'conversations.view_all',
        label: 'Ver todas as conversas (aba "Todos")',
      },
      { key: 'conversations.view_unassigned', label: 'Ver não atribuídas' },
      { key: 'conversations.assign', label: 'Atribuir conversas' },
      { key: 'conversations.bulk_actions', label: 'Ações em lote' },
    ],
  },
  {
    label: 'Pipeline / CRM',
    keys: [
      { key: 'pipeline.view', label: 'Ver pipelines' },
      { key: 'pipeline.manage', label: 'Criar/editar/excluir pipelines' },
      { key: 'deals.create', label: 'Criar deals' },
      { key: 'deals.edit', label: 'Editar deals' },
      { key: 'deals.delete', label: 'Excluir deals' },
      { key: 'deals.view_all', label: 'Ver todos os deals' },
    ],
  },
  {
    label: 'Contatos',
    keys: [
      { key: 'contacts.view', label: 'Ver contatos' },
      { key: 'contacts.create', label: 'Criar contatos' },
      { key: 'contacts.edit', label: 'Editar contatos' },
      { key: 'contacts.delete', label: 'Excluir contatos' },
      { key: 'contacts.import', label: 'Importar contatos (CSV)' },
    ],
  },
  {
    label: 'Calendário',
    keys: [
      { key: 'calendar.view', label: 'Ver calendário' },
      { key: 'calendar.manage', label: 'Criar/editar eventos' },
    ],
  },
  {
    label: 'Relatórios',
    keys: [{ key: 'reports.view', label: 'Ver relatórios' }],
  },
  {
    label: 'Base de Conhecimento',
    keys: [
      { key: 'knowledge_base.view', label: 'Ver artigos' },
      { key: 'knowledge_base.manage', label: 'Criar/editar artigos' },
    ],
  },
  {
    label: 'Agentes de IA',
    keys: [
      { key: 'ai_agents.view', label: 'Ver agentes IA' },
      { key: 'ai_agents.manage', label: 'Criar/editar agentes IA' },
    ],
  },
  {
    label: 'Chat Interno',
    keys: [
      { key: 'internal_chat.view', label: 'Acessar o chat interno' },
      { key: 'internal_chat.channel.create', label: 'Criar canais' },
      { key: 'internal_chat.channel.update', label: 'Editar canais' },
      { key: 'internal_chat.channel.delete', label: 'Excluir canais' },
      { key: 'internal_chat.channel.archive', label: 'Arquivar/desarquivar canais' },
      { key: 'internal_chat.members.invite', label: 'Convidar membros para canais' },
      { key: 'internal_chat.members.remove', label: 'Remover membros de canais' },
    ],
  },
  {
    label: 'Configurações',
    keys: [
      { key: 'settings.agents', label: 'Gerenciar agentes' },
      { key: 'settings.inboxes', label: 'Gerenciar caixas de entrada' },
      { key: 'settings.teams', label: 'Gerenciar equipes' },
      { key: 'settings.labels', label: 'Gerenciar labels' },
      { key: 'settings.roles', label: 'Gerenciar funções/permissões' },
      { key: 'settings.subscription', label: 'Ver dados da assinatura' },
    ],
  },
];

const visibleGroups = computed(() => {
  const allowed = availableKeys.value;
  if (!allowed || !allowed.length) return KEY_GROUPS;
  return KEY_GROUPS.map(group => ({
    ...group,
    keys: group.keys.filter(k => allowed.includes(k.key)),
  })).filter(group => group.keys.length > 0);
});

function toggleKey(key) {
  if (isReadOnly.value) return;
  const idx = selectedKeys.value.indexOf(key);
  if (idx === -1) selectedKeys.value.push(key);
  else selectedKeys.value.splice(idx, 1);
}

function handleSubmit() {
  emit('save', {
    name: name.value.trim(),
    description: description.value.trim(),
    permission_keys: selectedKeys.value,
  });
}

watch(
  () => props.role,
  role => {
    if (role) {
      name.value = role.name || '';
      description.value = role.description || '';
      selectedKeys.value = [...(role.permission_keys || [])].filter(
        k => k !== '*'
      );
    } else {
      name.value = '';
      description.value = '';
      selectedKeys.value = [];
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6 p-6 w-full">
    <!-- Header do modal -->
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{
          mode === 'create'
            ? $t('PIPELINE_PERMISSIONS.MODAL_TITLE_CREATE')
            : $t('PIPELINE_PERMISSIONS.MODAL_TITLE_EDIT', { name: role?.name })
        }}
      </h2>
    </div>

    <!-- Aviso readonly (role de Admin) -->
    <div
      v-if="isReadOnly"
      class="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-slate-500"
    >
      <span class="i-lucide-lock text-base" />
      {{ $t('PIPELINE_PERMISSIONS.SYSTEM_ROLE_NOTICE') }}
    </div>

    <!-- Aviso plano básico editando Agente -->
    <div
      v-if="!isPro && isEditingDefaultAgentRole"
      class="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-sm text-blue-700 dark:text-blue-300"
    >
      <span class="i-lucide-info text-base mt-0.5 shrink-0" />
      {{ $t('PIPELINE_PERMISSIONS.PLAN_RESTRICTION_NOTICE') }}
    </div>

    <!-- Nome e descrição (ocultos ao editar role padrão de Agente) -->
    <template v-if="!isEditingDefaultAgentRole && !isReadOnly">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">{{
          $t('PIPELINE_PERMISSIONS.FORM.NAME_LABEL')
        }}</label>
        <input
          v-model="name"
          type="text"
          class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          :placeholder="$t('PIPELINE_PERMISSIONS.FORM.NAME_PLACEHOLDER')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">{{
          $t('PIPELINE_PERMISSIONS.FORM.DESCRIPTION_LABEL')
        }}</label>
        <input
          v-model="description"
          type="text"
          class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
          :placeholder="$t('PIPELINE_PERMISSIONS.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </div>
    </template>

    <!-- Matriz de permissões -->
    <div
      v-if="!isReadOnly"
      class="flex flex-col gap-4 max-h-[50vh] overflow-y-auto"
    >
      <div
        v-for="group in visibleGroups"
        :key="group.label"
        class="flex flex-col gap-2"
      >
        <h4
          class="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500"
        >
          {{ group.label }}
        </h4>
        <div class="flex flex-col gap-1.5 pl-1">
          <label
            v-for="item in group.keys"
            :key="item.key"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="selectedKeys.includes(item.key)"
              class="w-4 h-4 rounded border-slate-300"
              @change="toggleKey(item.key)"
            />
            <span class="text-sm text-slate-700 dark:text-slate-300">{{
              item.label
            }}</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Ações -->
    <div
      class="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-700"
    >
      <Button variant="clear" slate @click="emit('close')">
        {{ $t('PIPELINE_PERMISSIONS.FORM.CANCEL') }}
      </Button>
      <Button v-if="!isReadOnly" @click="handleSubmit">
        {{
          mode === 'create'
            ? $t('PIPELINE_PERMISSIONS.FORM.CREATE')
            : $t('PIPELINE_PERMISSIONS.FORM.SAVE')
        }}
      </Button>
    </div>
  </div>
</template>
